import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import client, { fetcher } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertTriangle, Eye, FileText, Loader2, Info,
} from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  ResponsiveContainer, Tooltip,
} from "recharts";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { useAuth } from "@/contexts/AuthContext";
import IncidenciaDetailDrawer from "@/components/incidencias/IncidenciaDetailDrawer";
import "@/i18n/fase4";

const overflowIcon = new L.DivIcon({
  html: '<div style="width:14px;height:14px;border-radius:50%;background:#E76F51;border:2px solid #fff;box-shadow:0 0 4px rgba(0,0,0,.3)"></div>',
  iconSize: [14, 14], iconAnchor: [7, 7], className: "",
});
const obsIcon = new L.DivIcon({
  html: '<div style="width:10px;height:10px;border-radius:50%;background:#0B4C7A;border:2px solid #fff"></div>',
  iconSize: [10, 10], iconAnchor: [5, 5], className: "",
});

export default function Incidencias() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [exporting, setExporting] = useState(false);

  const statsQ = "?period=month";
  const { data: stats } = useSWR(`/incidencias/stats${statsQ}`, fetcher);
  const listQ = useMemo(() => {
    const p = new URLSearchParams({ limit: "20", page: String(page) });
    if (status !== "all") p.set("status", status);
    if (type !== "all") p.set("type", type);
    return p.toString();
  }, [status, type, page]);
  const { data: listData } = useSWR(`/incidencias/list?${listQ}`, fetcher);
  const { data: heatmap } = useSWR("/incidencias/heatmap-clean", fetcher);

  const kpi = (label, value, extraClass = "") => (
    <div className={`rounded-2xl border hairline bg-white p-6 ${extraClass}`}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-salnes-stone">{label}</div>
      <div className="font-serif text-4xl text-salnes-atlantic tabular-nums mt-1">{value}</div>
    </div>
  );

  const evolutionData = stats?.evolution || [];
  const byConcelloData = Object.entries(stats?.by_concello || {}).map(([name, count]) => ({ name, count }));

  const exportIncidenciasPdf = async () => {
    setExporting(true);
    try {
      const response = await client.post(
        "/informe/generate",
        {
          period: { from: new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10), to: new Date().toISOString().slice(0, 10) },
          concellos: [],
          modules: { portada: true, resumo_executivo: false, geoportal: false, esg: false, carbono: false, incidencias_ia: true, inventario_datos: false, conclusions: false },
          language: i18n.language,
        },
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `incidencias-salnes-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(t("fase4.inc_export_ok"));
    } catch {
      toast.error(t("fase4.inc_export_error"));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="incidencias-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-salnes-stone">{t("fase4.sidebar_incidencias")}</div>
          <h1 className="font-serif text-3xl text-salnes-ink">{t("fase4.inc_title")}</h1>
          <div className="text-sm text-salnes-stone mt-1">{t("fase4.inc_subtitle")}</div>
        </div>
        <Button
          onClick={exportIncidenciasPdf}
          disabled={exporting}
          className="rounded-full bg-salnes-atlantic hover:bg-salnes-atlantic/90"
          data-testid="incidencias-export-pdf-btn"
        >
          {exporting ? <Loader2 size={13} className="mr-2 animate-spin" /> : <FileText size={13} className="mr-2" />}
          {t("fase4.inc_export_pdf")}
        </Button>
      </div>

      {/* Privacy notice */}
      <div className="rounded-xl border border-salnes-coral/30 bg-salnes-coral/5 px-5 py-3 text-sm text-salnes-ink/90 mb-6 flex items-start gap-3">
        <Info size={16} className="text-salnes-coral shrink-0 mt-0.5" />
        <div className="flex-1">
          <div>{t("fase4.inc_privacy_notice")}</div>
          <a href="/transparencia" className="text-xs text-salnes-atlantic underline mt-1 inline-block">
            {t("fase4.inc_privacy_link")}
          </a>
        </div>
      </div>

      <Tabs defaultValue="panoramica" className="w-full">
        <TabsList className="mb-6 bg-white border hairline rounded-full h-11 p-1">
          <TabsTrigger value="panoramica" data-testid="inc-tab-panoramica" className="rounded-full data-[state=active]:bg-salnes-atlantic data-[state=active]:text-white">{t("fase4.inc_tab_panoramica")}</TabsTrigger>
          <TabsTrigger value="tabla" data-testid="inc-tab-tabla" className="rounded-full data-[state=active]:bg-salnes-atlantic data-[state=active]:text-white">{t("fase4.inc_tab_tabla")}</TabsTrigger>
          <TabsTrigger value="mapa" data-testid="inc-tab-mapa" className="rounded-full data-[state=active]:bg-salnes-atlantic data-[state=active]:text-white">{t("fase4.inc_tab_mapa")}</TabsTrigger>
          <TabsTrigger value="analise" data-testid="inc-tab-analise" className="rounded-full data-[state=active]:bg-salnes-atlantic data-[state=active]:text-white">{t("fase4.inc_tab_analise")}</TabsTrigger>
        </TabsList>

        {/* Panorámica */}
        <TabsContent value="panoramica">
          <div className="grid md:grid-cols-4 gap-4 mb-6">
            {kpi(t("fase4.inc_kpi_total"), stats?.total ?? "—")}
            {kpi(t("fase4.inc_kpi_overflow"), stats?.overflow ?? "—")}
            {kpi(t("fase4.inc_kpi_resolved_pct"), `${stats?.resolved_pct ?? 0}%`)}
            {kpi(t("fase4.inc_kpi_sla_pct"), `${stats?.sla_compliance_pct ?? 0}%`)}
          </div>
          <div className="rounded-2xl border hairline bg-white p-6 mb-6">
            <div className="text-[11px] uppercase tracking-[0.2em] text-salnes-stone mb-3">{t("fase4.inc_evolution_title")}</div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={evolutionData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#6B7280" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#6B7280" }} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12 }} />
                  <Line type="monotone" dataKey="count" stroke="#0B4C7A" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="rounded-2xl border hairline bg-white p-6">
            <div className="text-[11px] uppercase tracking-[0.2em] text-salnes-stone mb-3">{t("fase4.inc_by_concello")}</div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byConcelloData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis type="number" tick={{ fontSize: 10, fill: "#6B7280" }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: "#6B7280" }} width={120} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12 }} />
                  <Bar dataKey="count" fill="#10B981" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </TabsContent>

        {/* Tabla */}
        <TabsContent value="tabla">
          <div className="flex gap-3 mb-4">
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-[180px]" data-testid="inc-filter-status"><SelectValue placeholder={t("fase4.inc_filter_status")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("fase4.inc_filter_all")}</SelectItem>
                <SelectItem value="NUEVA">NUEVA</SelectItem>
                <SelectItem value="EN_TRIAGE">EN_TRIAGE</SelectItem>
                <SelectItem value="ASIGNADA">ASIGNADA</SelectItem>
                <SelectItem value="EN_RUTA">EN_RUTA</SelectItem>
                <SelectItem value="RESUELTA">RESUELTA</SelectItem>
                <SelectItem value="CERRADA">CERRADA</SelectItem>
              </SelectContent>
            </Select>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="w-[180px]" data-testid="inc-filter-type"><SelectValue placeholder={t("fase4.inc_filter_type")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("fase4.inc_filter_all")}</SelectItem>
                <SelectItem value="overflow">{t("fase4.inc_type_overflow")}</SelectItem>
                <SelectItem value="observation">{t("fase4.inc_type_observation")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="rounded-2xl border hairline bg-white overflow-hidden">
            <table className="w-full text-sm" data-testid="incidencias-table">
              <thead className="bg-salnes-sand/40 text-salnes-stone text-[10px] uppercase tracking-[0.18em]">
                <tr>
                  <th className="text-left px-4 py-3">{t("fase4.inc_th_date")}</th>
                  <th className="text-left px-4 py-3">{t("fase4.inc_th_concello")}</th>
                  <th className="text-left px-4 py-3">{t("fase4.inc_th_jetson")}</th>
                  <th className="text-left px-4 py-3">{t("fase4.inc_th_type")}</th>
                  <th className="text-left px-4 py-3">{t("fase4.inc_th_status")}</th>
                  <th className="text-right px-4 py-3">{t("fase4.inc_th_actions")}</th>
                </tr>
              </thead>
              <tbody className="divide-y hairline">
                {(listData?.items || []).map((d) => (
                  <tr key={d.object_id} data-testid={`inc-row-${d.object_id}`} className="hover:bg-salnes-sand/20">
                    <td className="px-4 py-3 text-salnes-ink text-[13px]">{d.detection_date?.slice(0, 16).replace("T", " ")}</td>
                    <td className="px-4 py-3">{d.municipality || "—"}</td>
                    <td className="px-4 py-3 tabular-nums">{d.jetson_id ?? "—"}</td>
                    <td className="px-4 py-3">{d.overflow ? <Badge className="bg-salnes-coral text-white text-[10px]">{t("fase4.inc_type_overflow")}</Badge> : <Badge className="bg-salnes-atlantic text-white text-[10px]">{t("fase4.inc_type_observation")}</Badge>}</td>
                    <td className="px-4 py-3"><Badge variant="outline" className="text-[10px]">{d.workflow_status}</Badge></td>
                    <td className="px-4 py-3 text-right">
                      <button data-testid={`inc-view-${d.object_id}`} onClick={() => setSelected(d.object_id)} className="text-salnes-atlantic hover:underline text-xs inline-flex items-center gap-1">
                        <Eye size={12} /> {t("fase4.inc_view_detail")}
                      </button>
                    </td>
                  </tr>
                ))}
                {(!listData || listData.items.length === 0) && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-salnes-stone text-sm italic">{t("fase4.inc_no_data")}</td></tr>
                )}
              </tbody>
            </table>
            {listData && listData.pages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t hairline text-xs text-salnes-stone">
                <div>{listData.total} · página {listData.page}/{listData.pages}</div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>‹</Button>
                  <Button size="sm" variant="outline" disabled={page >= listData.pages} onClick={() => setPage((p) => p + 1)}>›</Button>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Mapa */}
        <TabsContent value="mapa">
          <div className="rounded-2xl border hairline bg-white overflow-hidden h-[600px]" data-testid="inc-map">
            <MapContainer center={[42.55, -8.78]} zoom={11} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
              <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap · CARTO' />
              {(heatmap?.features || []).map((f, idx) => {
                const [lon, lat] = f.geometry.coordinates;
                const isOver = f.properties.overflow;
                return (
                  <Marker key={idx} position={[lat, lon]} icon={isOver ? overflowIcon : obsIcon}>
                    <Popup>
                      <div className="text-xs">
                        <div className="font-medium">#{f.properties.object_id}</div>
                        <div>{f.properties.workflow_status}</div>
                        <div className="text-salnes-stone">{f.properties.detection_date?.slice(0, 16).replace("T", " ")}</div>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          </div>
          <div className="text-xs text-salnes-stone mt-2 italic">
            Fonte: /api/incidencias/heatmap-clean · {heatmap?.count || 0} puntos filtrados a bbox O Salnés
          </div>
        </TabsContent>

        {/* Análise */}
        <TabsContent value="analise">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-2xl border hairline bg-white p-6">
              <div className="text-[11px] uppercase tracking-[0.2em] text-salnes-stone mb-3">{t("fase4.inc_by_jetson")}</div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={Object.entries(stats?.by_jetson || {}).map(([id, n]) => ({ jetson: `J${id}`, n }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="jetson" tick={{ fontSize: 10, fill: "#6B7280" }} />
                    <YAxis tick={{ fontSize: 10, fill: "#6B7280" }} />
                    <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12 }} />
                    <Bar dataKey="n" fill="#0B4C7A" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="rounded-2xl border hairline bg-white p-6">
              <div className="text-[11px] uppercase tracking-[0.2em] text-salnes-stone mb-3">{t("fase4.inc_by_concello")}</div>
              <div className="space-y-2">
                {Object.entries(stats?.by_concello || {}).sort((a, b) => b[1] - a[1]).slice(0, 10).map(([name, n]) => (
                  <div key={name} className="flex items-center gap-3">
                    <div className="w-32 text-xs text-salnes-ink truncate">{name}</div>
                    <div className="flex-1 h-2 bg-salnes-sand/50 rounded-full overflow-hidden">
                      <div className="h-full bg-salnes-emerald" style={{ width: `${Math.min(100, (n / (stats?.total || 1)) * 100)}%` }} />
                    </div>
                    <div className="w-10 text-right text-xs tabular-nums text-salnes-stone">{n}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <IncidenciaDetailDrawer
        objectId={selected}
        onClose={() => setSelected(null)}
        role={user?.role}
      />
    </div>
  );
}
